import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Transactions
export const createTransaction = (data) => api.post('/transactions', data);
export const getTransactions = (params) => api.get('/transactions', { params });
export const getTransaction = (id) => api.get(`/transactions/${id}`);
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data);
export const deleteTransaction = (id) => api.delete(`/transactions/${id}`);

// Budgets
export const createBudget = (data) => api.post('/budgets', data);
export const getBudgets = () => api.get('/budgets');
export const deleteBudget = (category) => api.delete(`/budgets/${category}`);

// Objectives
export const createObjective = (data) => api.post('/objectives', data);
export const getObjectives = () => api.get('/objectives');
export const getPausedObjectives = () => api.get('/objectives/paused');
export const getObjectivesStats = () => api.get('/objectives/stats');
export const getSuggestedContribution = (id) => api.get(`/objectives/${id}/suggested-contribution`);
export const updateObjective = (id, data) => api.patch(`/objectives/${id}`, data);
export const deleteObjective = (id) => api.delete(`/objectives/${id}`);
export const contributeToObjective = (id, amount) => api.post(`/objectives/${id}/contribute`, { amount });
export const overrideObjectiveContribution = (id, amount) => api.post(`/objectives/${id}/override-contribution`, { amount });
export const processAutoObjectiveContributions = () => api.post('/objectives/process-auto');
export const withdrawFromObjective = (id, amount, reason) => api.post(`/objectives/${id}/withdraw`, { amount, reason });
export const getAvailableSavings = () => api.get('/objectives/available-savings');

// Alerts
export const getAlerts = () => api.get('/alerts');
export const getOverdraftCheck = () => api.get('/alerts/overdraft-check');

// Negative Balance Carryover
export const getNegativeBalanceCarryover = () => api.get('/balance/carryover');
export const createNegativeBalanceCarryover = (amount, budgetType, month) => api.post('/balance/carryover/create', { amount, budget_type: budgetType, month });
export const deductFromCarryover = (carryoverId, amount) => api.post(`/balance/carryover/${carryoverId}/deduct`, { amount });
export const ignoreCarryover = (carryoverId) => api.post(`/balance/carryover/${carryoverId}/ignore`);
export const checkEndOfMonthBalance = () => api.post('/balance/check-end-of-month');

// Challenges
export const getTodayChallenge = () => api.get('/challenges/today');
export const completeChallenge = (id, answer) => api.post(`/challenges/${id}/complete`, { answer });

// AI
export const categorizeTransaction = (data) => api.post('/ai/categorize', data);
export const getAIAdvice = (data) => api.post('/ai/advice', data);

// User
export const getUserStats = () => api.get('/users/stats');
export const updateProfile = (data) => api.patch('/users/profile', data);

// Income Adjustments
export const getIncomeAdjustments = () => api.get('/income/adjustments');
export const addIncomeAdjustment = (amount, reason, description = null) => 
  api.post('/income/adjustments', { amount, reason, description });
export const deleteIncomeAdjustment = (adjustmentId) => api.delete(`/income/adjustments/${adjustmentId}`);

// Income Sources (multi-revenues)
export const getIncomeSourceTypes = () => api.get('/income/source-types');
export const getIncomeSources = (month = null) => api.get('/income/sources', { params: month ? { month } : {} });
export const createIncomeSource = (data) => api.post('/income/sources', data);
export const updateIncomeSource = (sourceId, data) => api.put(`/income/sources/${sourceId}`, data);
export const deleteIncomeSource = (sourceId) => api.delete(`/income/sources/${sourceId}`);
export const getMonthlyIncomeTotal = (month = null) => api.get('/income/monthly-total', { params: month ? { month } : {} });
export const getIncomeHistory = (months = 12, scope = 'personal') => api.get('/income/history', { params: { months, scope } });

// Categories
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);
export const getCustomEmojis = () => api.get('/user/category-emojis');
export const updateCustomEmoji = (categoryId, emoji) => api.put('/user/category-emojis', { category_id: categoryId, emoji });
export const deleteCustomEmoji = (categoryId) => api.delete(`/user/category-emojis/${categoryId}`);

// Admin - System Emojis
export const getSystemEmojis = () => api.get('/admin/system-emojis');
export const updateSystemEmoji = (categoryId, emoji) => api.put('/admin/system-emojis', { category_id: categoryId, emoji });

export const getAIPersonalities = () => api.get('/ai-personalities');

// Referral
export const getReferralInfo = () => api.get('/referral');
export const applyReferralCode = (code) => api.post('/referral/apply', { referral_code: code });
export const getReferralLeaderboard = () => api.get('/referral/leaderboard');

// Auth - Unified login (email or username)
export const loginWithCredentials = (identifier, password) => api.post('/auth/login', { identifier, password });
export const loginWithEmail = (email, password) => api.post('/auth/login', { identifier: email, password });
export const loginWithUsername = (username, password) => api.post('/auth/login', { identifier: username, password });
export const registerUser = (email, password, name, referral_code = null) => api.post('/auth/register', { email, password, name, referral_code });

// Auth - Password Reset
export const requestPasswordReset = (email) => api.post('/auth/forgot-password', { email });
export const verifyResetToken = (token) => api.get(`/auth/verify-reset-token/${token}`);
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// Auth - Username/Password for children (created by parents)
export const checkUsername = (username) => api.post('/auth/check-username', { username });
export const validatePassword = (password) => api.post('/auth/validate-password', { password });
export const createChildAccount = (data) => api.post('/auth/create-child-account', data);

// Auth - Invitations
export const getInvitation = (invitationId) => api.get(`/invitations/${invitationId}`);

// Family invitation
export const inviteChildByEmail = (data) => api.post('/auth/invite-child', data);

// OCR
export const scanReceipt = (formData) => api.post('/ocr/scan', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Family
export const createFamily = (data) => api.post('/family', data);
export const getFamily = () => api.get('/family');
export const deleteFamily = () => api.delete('/family');
export const updateFamilySettings = (data) => api.patch('/family/settings', data);
export const addFamilyMember = (data) => api.post('/family/members', data); // Add member directly (no email needed)
export const updateFamilyMember = (memberId, data) => api.patch(`/family/members/${memberId}`, data);
export const removeFamilyMember = (memberId) => api.delete(`/family/members/${memberId}`);
export const inviteToFamily = (data) => api.post('/family/invite', data); // Invite with email
export const cancelFamilyInvite = (inviteId) => api.delete(`/family/invite/${inviteId}`);
export const resendFamilyInvite = (inviteId) => api.post(`/family/invite/${inviteId}/resend`);
export const transferFamilyOwnership = (newOwnerMemberId) => api.post('/family/transfer-ownership', { new_owner_member_id: newOwnerMemberId });
export const joinFamily = (inviteCode) => api.post('/family/join', { invite_code: inviteCode });
export const leaveFamily = () => api.post('/family/leave');
export const getFamilyStats = () => api.get('/family/stats');
export const getFamilyTransactions = (params) => api.get('/family/transactions', { params });
export const getSpendingByMember = (params) => api.get('/family/spending-by-member', { params });

// Pocket Money (Argent de poche)
export const transferPocketMoney = (data) => api.post('/family/pocket-money/transfer', data);
export const getPocketMoneyBalance = () => api.get('/family/pocket-money/balance');
export const requestPocketMoney = (data) => api.post('/family/pocket-money/request', data);
export const getPocketMoneyRequests = (status) => api.get('/family/pocket-money/requests', { params: { status } });
export const respondToPocketMoneyRequest = (requestId, approved) => api.post(`/family/pocket-money/requests/${requestId}/respond`, null, { params: { approved } });
export const getChildTransactions = (memberId) => api.get(`/family/pocket-money/transactions/${memberId}`);
export const addChildExpense = (data) => api.post('/family/pocket-money/expense', data);
export const processAutoAllowance = () => api.post('/family/pocket-money/auto-allowance');
export const updatePocketMoneySettings = (memberId, data) => api.patch(`/family/pocket-money/settings/${memberId}`, null, { params: data });
export const getPocketMoneyStats = () => api.get('/family/pocket-money/stats');

// Subscriptions (Abonnements)
export const getSubscriptions = () => api.get('/subscriptions');
export const getUpcomingSubscriptions = (window = '30d') => api.get('/subscriptions/upcoming', { params: { window } });
export const createSubscription = (data) => api.post('/subscriptions', data);
export const updateSubscription = (subscriptionId, data) => api.patch(`/subscriptions/${subscriptionId}`, data);
export const deleteSubscription = (subscriptionId) => api.delete(`/subscriptions/${subscriptionId}`);
export const processSubscriptions = () => api.post('/subscriptions/process');
export const getSubscriptionStats = () => api.get('/subscriptions/stats');

// Pot Commun (Common Pot)
export const updateMyContribution = (data) => api.patch('/family/my-contribution', data);
export const contributeToPotCommun = (data) => api.post('/family/pot-commun/contribute', data);
export const getPotCommunStatus = () => api.get('/family/pot-commun');
export const validateInviteCode = (code) => api.get(`/family/validate-invite/${code}`);

// Shopping Intelligent
export const analyzeReceiptForShopping = (data) => api.post('/shopping/analyze-receipt', data);
export const comparePrices = (params) => api.get('/shopping/compare-prices', { params });
export const getPriceHistory = (productName, days = 180) => api.get('/shopping/price-history', { params: { product_name: productName, days } });
export const getShoppingSuggestions = () => api.get('/shopping/suggestions');

// Email Auto-Scan
export const getEmailAuthUrl = (provider) => api.get(`/email/connect/${provider}`);
export const scanEmails = (provider, days = 30) => api.post('/email/scan', null, { params: { provider, days } });
export const parseEmailReceipt = (emailData) => api.post('/email/parse-receipt', emailData);
export const getEmailReceipts = (imported = null) => api.get('/email/receipts', { params: { imported } });
export const importEmailReceipt = (receiptId) => api.post(`/email/receipts/${receiptId}/import`);

// Export Fiscal
export const exportFiscalData = (year, format = 'json') => api.get('/export/fiscal', { params: { year, format } });
export const exportFiscalPdf = (year) => api.get('/export/fiscal/pdf', { params: { year } });

// Recurring Expenses
export const detectRecurring = () => api.get('/recurring/detect');
export const getRecurring = () => api.get('/recurring');
export const confirmRecurring = (data) => api.post('/recurring/confirm', data);
export const deleteRecurring = (id) => api.delete(`/recurring/${id}`);

// Subscription Management
export const changeSubscription = (newPlan) => api.post('/subscription/change', { new_plan: newPlan });
export const cancelSubscription = () => api.post('/subscription/cancel');
export const getSubscriptionStatus = () => api.get('/subscription/status');
export const getSubscriptionPlans = () => api.get('/subscription/plans');
export const getSubscriptionUsageDetails = () => api.get('/subscription/usage-details');

// Installment Payments
export const getInstallmentGroups = () => api.get('/transactions/installments');
export const deleteInstallmentGroup = (groupId) => api.delete(`/transactions/installments/${groupId}`);

// Notifications
export const getNotificationPreferences = () => api.get('/notifications/preferences');
export const updateNotificationPreferences = (prefs) => api.put('/notifications/preferences', prefs);
export const subscribePush = (subscription) => api.post('/notifications/subscribe', subscription);
export const unsubscribePush = () => api.delete('/notifications/unsubscribe');
export const getUpcomingInstallments = () => api.get('/notifications/upcoming-installments');
export const getUpcomingSubscriptionNotifications = () => api.get('/notifications/upcoming-subscriptions');
export const checkAlertsForNotification = () => api.get('/notifications/check-alerts');

// LLM Chat
export const getLLMProviders = () => api.get('/llm/providers');
export const sendLLMMessage = (message, provider = null, model = null, sessionId = null) => 
  api.post('/llm/chat', { message, provider, model, session_id: sessionId });
export const getLLMSessions = (limit = 10) => api.get('/llm/sessions', { params: { limit } });
export const getLLMSessionHistory = (sessionId) => api.get(`/llm/sessions/${sessionId}`);
export const deleteLLMSession = (sessionId) => api.delete(`/llm/sessions/${sessionId}`);
export const updateLLMSettings = (provider, model = null) => 
  api.put('/llm/settings', { default_provider: provider, default_model: model });
export const analyzeSpendingWithAI = () => api.post('/llm/analyze-spending');
export const suggestBudgetWithAI = () => api.post('/llm/suggest-budget');

// Payment Methods
export const getPaymentMethods = () => api.get('/payment-methods');

export default api;
